import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FormattedMessage,
  MessageDescriptor,
  defineMessages,
  useIntl,
} from "react-intl";
import { UpdateUserRoleBodyRole } from "~/api/model";
import { getListUsersQueryKey, useInviteUser } from "~/api/users/users";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useSettingsParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";

interface InviteUserDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type FormData = {
  name: string;
  email: string;
  role: UpdateUserRoleBodyRole;
};

enum InviteDialogStep {
  InviteUser = 1,
  Success = 2,
  Error = 3,
}

const KNOWN_ERROR_KEYS: Record<string, string> = {
  "A user with this email is already a member of your organization":
    "settings.team.invite.error.alreadyMember",
  "A user with this email is already registered in another organization":
    "settings.team.invite.error.anotherOrg",
};

const roles: Record<UpdateUserRoleBodyRole, MessageDescriptor> = defineMessages(
  {
    OWNER: {
      id: "settings.team.role.owner",
      defaultMessage: "Admin",
    },
    MEMBER: {
      id: "settings.team.role.member",
      defaultMessage: "Member",
    },
  }
);

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const InviteUserDialog: FC<InviteUserDialogProps> = ({
  open,
  setOpen,
}) => {
  const [step, setStep] = useState<InviteDialogStep>(
    InviteDialogStep.InviteUser
  );
  const { organizationId } = useSettingsParams();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      role: "MEMBER",
    },
    mode: "onTouched",
  });

  const [errorMessageId, setErrorMessageId] = useState<string | null>(null);
  const { mutate: inviteUser, isPending } = useInviteUser();

  const onSubmit = (data: FormData) => {
    inviteUser(
      { organizationId, data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListUsersQueryKey(organizationId),
          });
          setStep(InviteDialogStep.Success);
        },
        onError: (error: unknown) => {
          let knownKey: string | null = null;
          if (axios.isAxiosError<{ message?: string }>(error)) {
            const serverMessage = error.response?.data?.message;
            if (serverMessage && KNOWN_ERROR_KEYS[serverMessage]) {
              knownKey = KNOWN_ERROR_KEYS[serverMessage];
            }
          }
          setErrorMessageId(knownKey);
          setStep(InviteDialogStep.Error);
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {step === InviteDialogStep.InviteUser && (
              <FormattedMessage
                id="settings.team.inviteMember"
                defaultMessage="Invite Member"
              />
            )}
            {step === InviteDialogStep.Success && (
              <FormattedMessage id="success" defaultMessage="Success" />
            )}
            {step === InviteDialogStep.Error && (
              <FormattedMessage id="error" defaultMessage="Error" />
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {step === InviteDialogStep.InviteUser && (
              <FormattedMessage
                id="settings.team.inviteMember.hint"
                defaultMessage="Invite a team member, specify their email, name, and role"
              />
            )}
            {step === InviteDialogStep.Success && (
              <FormattedMessage
                id="settings.team.invite.success"
                defaultMessage="The invitation was successfully sent."
              />
            )}
            {step === InviteDialogStep.Error &&
              (errorMessageId ? (
                <FormattedMessage id={errorMessageId} />
              ) : (
                <FormattedMessage
                  id="settings.team.invite.error"
                  defaultMessage="Something went wrong. Please try again later."
                />
              ))}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {step === InviteDialogStep.InviteUser && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full flex flex-col gap-4"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="inviteEmail">
                <FormattedMessage
                  id="settings.team.table.email"
                  defaultMessage="Email"
                />
              </Label>
              <Input
                id="inviteEmail"
                onClick={() => {
                  trackEvent({
                    eventName: "app/settings_team/invite.input_email/click",
                  });
                }}
                placeholder="example@email.com"
                className="shadow-none"
                {...register("email", {
                  required: intl.formatMessage({
                    id: "settings.team.emailError",
                    defaultMessage: "Email is required",
                  }),
                  pattern: {
                    value: EMAIL_REGEX,
                    message: intl.formatMessage({
                      id: "settings.team.emailFormatError",
                      defaultMessage: "Invalid email format",
                    }),
                  },
                })}
              />
              {touchedFields.email && errors.email && (
                <span className="mt-[-2px] text-xs text-destructive">
                  {errors.email.message}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="inviteName">
                <FormattedMessage
                  id="settings.team.table.name"
                  defaultMessage="Name"
                />
              </Label>
              <Input
                id="inviteName"
                onClick={() => {
                  trackEvent({
                    eventName: "app/settings_team/invite.input_name/click",
                  });
                }}
                placeholder={intl.formatMessage({
                  id: "settings.team.table.name",
                  defaultMessage: "Name",
                })}
                className="shadow-none"
                {...register("name", {
                  required: intl.formatMessage({
                    id: "settings.team.nameError",
                    defaultMessage: "Name is required",
                  }),
                })}
              />
              {touchedFields.name && errors.name && (
                <span className="mt-[-2px] text-xs text-destructive">
                  {errors.name.message}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label>
                <FormattedMessage
                  id="settings.team.memberRole"
                  defaultMessage="Role"
                />
              </Label>
              <Select
                onValueChange={(value) =>
                  setValue("role", value as UpdateUserRoleBodyRole)
                }
                defaultValue="MEMBER"
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {Object.entries(roles).map(([role, descriptor]) => (
                      <SelectItem key={role} value={role}>
                        {intl.formatMessage(descriptor)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  <FormattedMessage id="cancel" defaultMessage="Cancel" />
                </Button>
                <Button type="submit" disabled={isPending}>
                  <FormattedMessage
                    id="settings.team.invite"
                    defaultMessage="Invite"
                  />
                </Button>
              </div>
            </div>
          </form>
        )}
        {step !== InviteDialogStep.InviteUser && (
          <Button
            variant="outline"
            className="ml-auto"
            onClick={() => setOpen(false)}
          >
            <FormattedMessage id="close" defaultMessage="Close" />
          </Button>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
